import moment from "moment";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { routeConstant } from "shared/routes/routeConstant";
import { ProjectDetailTabs } from "shared/utils/enum";
import {
  LeaveProjectDesignerService,
  ProjectArchiveService,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  formatProjectDesignerRole,
} from "shared/services/project";
import { handleErrors, getProjectStatusLabel } from "shared/utils/helper";
import { toastMessage } from "shared/components/common/toast";
import { Icons } from "assets";
import classNames from "classnames";
import CustomStatusBar from "../customStatusBar";
import styles from "./style.module.scss";
import CustomButton from "../customButton";
import CustomIconButton from "../customIconButton";
import CustomBadge from "../customBadge";
import ConfirmationModal from "shared/modal/confirmation";
import JoinProjectModal, { JoinRole } from "shared/modal/joinProjectModal";
import FinalPlansModal from "shared/modal/finalPlansModal";
import {
  BadgeViewer,
  getRevisionStatusLabel,
  getRevisionStatusType,
} from "./revisionStatusLabel";

interface CustomProjectCardProps {
  id?: number;
  image: string | any;
  title: string;
  currentRevision:
  | number
  | string
  | { title?: string; revision_id?: string | number;[key: string]: any }
  | null
  | undefined;
  status?: string;
  onPrimaryAction?: () => void;
  onJoinSuccess?: (role: JoinRole) => void;
  onLeaveSuccess?: () => void;
  isDesignerJoined?: boolean;
  onProceedToNextPhase?: () => void;
  onChatIconClick?: () => void;
  isArchived?: boolean;
  onArchiveToggle?: () => void;
  showArchiveButton?: boolean;
  role?: string | null;
  hasUnreadChat?: boolean;
  // The viewer's own membership on this project is view-only (collaborator
  // portals pass this to classify the viewer as an observer rather than an
  // actor for badge wording).
  viewerViewOnly?: boolean;
}


const CustomProjectCard = ({
  id,
  image,
  title,
  currentRevision,
  status,
  onPrimaryAction,
  onJoinSuccess,
  onLeaveSuccess,
  onChatIconClick,
  isDesignerJoined = false,
  isArchived = false,
  onArchiveToggle,
  showArchiveButton = false,
  role = null,
  hasUnreadChat = false,
  viewerViewOnly = false,
}: CustomProjectCardProps) => {
  const [activeStep, setActiveStep] = useState(-1);
  const [imageError, setImageError] = useState(false);
  const [completeModalOpen, setCompleteModalOpen] = useState(false);
  const [joinModalOpen, setJoinModalOpen] = useState(false);
  const [leaveModalOpen, setLeaveModalOpen] = useState(false);
  const [hasJoined, setHasJoined] = useState(isDesignerJoined);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archived, setArchived] = useState(isArchived);
  const location = useLocation();
  const pathname = location.pathname;
  const navigate = useNavigate();

  const isDesigner = pathname.includes("/designer");
  /* eslint-disable @typescript-eslint/no-unused-vars */
  const isClient = pathname.includes("/client");
  const isBuilder = pathname.includes("/builder");
  const isCollaborator = pathname.includes("/collaborator");
  /* eslint-enable @typescript-eslint/no-unused-vars */
  const isOnDetailPage = /\/projects\/[^/]+/.test(pathname);

  const roleLower = (role || "").toLowerCase();
  // Per client ruling 2026-07-18: the plain "designer" role carries the same
  // permissions as "both", so every designer role can complete the project.
  const isFinalReviewerOrBoth =
    roleLower === "designer" ||
    roleLower === "final_reviewer" ||
    roleLower === "both";
  const isDesignerOrBoth = roleLower === "designer" || roleLower === "both";
  const statusNormalized = (status || "").toLowerCase().replace(/[-\s]/g, "_");
  const isFinalReviewStatus = statusNormalized.includes("final_review");
  const isPreliminaryOrLater =
    statusNormalized.includes("preliminary") ||
    statusNormalized.includes("final_review") ||
    statusNormalized.includes("completed");
  const canShowCompleteProjectButton =
    isDesigner &&
    activeStep === 3 &&
    isFinalReviewerOrBoth &&
    isFinalReviewStatus &&
    !archived;
  const canShowUploadRevision =
    isDesigner && role && isDesignerOrBoth;

  const getProjectDetailPath = (): string => {
    if (isDesigner) {
      return routeConstant.designer.projectDetail.path.replace(
        ":id",
        String(id || ""),
      );
    }
    if (isClient) {
      return routeConstant.client.projectDetail.path.replace(
        ":id",
        String(id || ""),
      );
    }
    if (isBuilder) {
      return routeConstant.builder.projectDetail.path.replace(
        ":id",
        String(id || ""),
      );
    }
    if (isCollaborator) {
      return routeConstant.collaborator.projectDetail.path.replace(
        ":id",
        String(id || ""),
      );
    }
    return routeConstant.designer.projectDetail.path.replace(
      ":id",
      String(id || ""),
    );
  };

  const handleChatIconClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!id) return;

    if (onChatIconClick) {
      onChatIconClick();
      return;
    }

    navigate(getProjectDetailPath(), {
      state: {
        fromChatBoard: true,
        projectData: (location.state as any)?.projectData,
      },
    });
  };

  const isWaitingForClientResponse = () => {
    if (typeof currentRevision === "object" && currentRevision !== null) {
      const revisionStatus = currentRevision.status || "";
      const statusLower = revisionStatus.toLowerCase().replace(/[_\s-]/g, "");
      return statusLower.includes("waitingforclientresponse");
    }
    return false;
  };

  const isRevisionInReview = () => {
    if (typeof currentRevision === "object" && currentRevision !== null) {
      const revisionStatus = currentRevision.status || "";
      return revisionStatus.toLowerCase().replace(/[_\s-]/g, "") === "inreview";
    }
    return false;
  };

  const showDesignerNewWorkBubble = isDesigner && isRevisionInReview();

  // Set by the API only while the revision awaits the client, and only for the
  // project's own client — a collaborator opening it does not count.
  const clientViewedAt =
    typeof currentRevision === "object" && currentRevision !== null
      ? currentRevision.client_viewed_at
      : null;

  const currentRevisionStatus =
    typeof currentRevision === "object" && currentRevision !== null
      ? (currentRevision.status as string) || ""
      : "";
  const hasRevision = Boolean(currentRevision);
  const badgeViewer: BadgeViewer = isDesigner
    ? "designer"
    : isBuilder || (isCollaborator && viewerViewOnly)
      ? "observer"
      : "actor";
  const revisionStatusLabel = getRevisionStatusLabel(
    currentRevisionStatus,
    status,
    badgeViewer,
    hasRevision,
  );
  const revisionStatusType = getRevisionStatusType(
    currentRevisionStatus,
    status,
    badgeViewer,
    hasRevision,
  );

  const steps = [
    { label: "Consultation" },
    { label: "Preliminary" },
    { label: "Preliminary Finals" },
    { label: "Final Review" },
    { label: "Complete" },
  ];

  const getStepFromStatus = (status?: string): number => {
    if (!status) return -1;
    const statusLower = status.toLowerCase().replace(/[_\s-]/g, "");

    if (statusLower.includes("onboarding")) return -1;
    if (statusLower.includes("consultation")) return 0;
    if (statusLower.includes("preliminaryfinal")) return 2;
    if (statusLower.includes("preliminary")) return 1;
    if (statusLower.includes("finalreview") || statusLower.includes("final"))
      return 3;
    if (statusLower.includes("complete")) return steps.length;

    return -1;
  };

  useEffect(() => {
    const stepIndex = getStepFromStatus(status);
    setActiveStep(stepIndex);
  }, [status]);

  const handleProceedToCompleteProject = () => {
    setCompleteModalOpen(true);
  };

  // Completion happens inside FinalPlansModal (final plans upload + confirm
  // + complete call); the card only reacts to its success.
  const handleCompleteProjectSuccess = () => {
    setCompleteModalOpen(false);
    toastMessage(
      "success",
      "Final plans submitted — project completed successfully",
    );
    navigate(routeConstant.designer.home.path);
  };

  const getRevisionValue = (): string => {
    if (!currentRevision) return "N/A";
    if (
      typeof currentRevision === "string" ||
      typeof currentRevision === "number"
    ) {
      return String(currentRevision);
    }
    if (typeof currentRevision === "object" && currentRevision !== null) {
      return (
        currentRevision.title ||
        String(currentRevision.revision_id || "") ||
        "N/A"
      );
    }
    return "N/A";
  };

  const getInitials = (name: string): string => {
    if (!name) return "??";
    const words = name
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    if (words.length === 1 && words[0].length >= 2) {
      return words[0].substring(0, 2).toUpperCase();
    }
    if (words.length === 1 && words[0].length === 1) {
      return (words[0][0] + words[0][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase() || "??";
  };

  const hasValidImage = () => {
    if (imageError) return false;
    if (image === null || image === undefined) return false;
    if (typeof image !== "string") return false;
    const trimmed = String(image).trim();
    if (trimmed === "" || trimmed === "null" || trimmed === "undefined")
      return false;
    return trimmed.length > 0;
  };

  const handleImageError = () => {
    setImageError(true);
  };

  useEffect(() => {
    setImageError(false);
  }, [image]);

  useEffect(() => {
    setHasJoined(isDesignerJoined);
  }, [isDesignerJoined]);

  useEffect(() => {
    setArchived(isArchived);
  }, [isArchived]);

  const handleProceedToArchive = () => {
    setArchiveModalOpen(true);
  };

  const handleConfirmArchiveToggle = () => {
    if (!id) return;
    setArchiveLoading(true);
    ProjectArchiveService(id, true)
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          setArchiveModalOpen(false);
          setArchived(true);
          toastMessage("success", "Project archived successfully");
          onArchiveToggle?.();
        } else {
          handleErrors(res);
        }
      })
      .catch((err) => handleErrors(err))
      .finally(() => setArchiveLoading(false));
  };

  const handleProceedToLeaveProject = () => {
    setLeaveModalOpen(true);
  };

  const handleConfirmLeaveProject = () => {
    if (!id) return;
    setLeaveLoading(true);
    LeaveProjectDesignerService(id)
      .then((res) => {
        if (res.status === 200 || res.status === 204) {
          setLeaveModalOpen(false);
          setHasJoined(false);
          toastMessage("success", "Successfully left the project");
          onLeaveSuccess?.();
        } else {
          handleErrors(res);
        }
      })
      .catch((err) => handleErrors(err))
      .finally(() => setLeaveLoading(false));
  };

  return (
    <div className={classNames(styles.projectCard, "flex gap-3")}>
      <button onClick={onPrimaryAction} className={styles.imgContainer}>
        {hasValidImage() ? (
          <img src={image as string} alt="Project" onError={handleImageError} />
        ) : (
          <div className={styles.initialsContainer}>
            <span className={styles.initialsText}>{getInitials(title)}</span>
          </div>
        )}
      </button>

      <div className={styles.contentContainer}>
        <div className="flex items-center justify-between flex-wrap-reverse gap-3">
          <div className={styles.titleRow}>
            <h3 onClick={onPrimaryAction} className={styles.title}>
              {title}
            </h3>
            {/* Hidden to keep the dash bar less busy.
            {role ? (
              <CustomBadge
                title={
                  role.toLowerCase() === "both"
                    ? `Joined as ${formatProjectDesignerRole(role)} (Designer and Final Reviewer)`
                    : `Joined as ${formatProjectDesignerRole(role)}`
                }
                statusType="inprogress"
              />
            ) : null}
            */}
          </div>

          <div className="flex items-center gap-2">
            <div className={styles.desktopNotification}>
              <button
                onClick={handleChatIconClick}
                className={classNames(
                  styles.notificationIcon,
                  "relative cursor-pointer",
                )}
                type="button"
              >
                <Icons.ChatNotificationIcon
                  className={styles.chatDotsAnimated}
                />
                {showDesignerNewWorkBubble && (
                  <span
                    className={styles.newRevisionDot}
                    aria-label="New client revision request"
                  />
                )}
                {hasUnreadChat && (
                  <span
                    className={styles.chatUnreadDot}
                    aria-label="New chat message"
                  />
                )}
              </button>
            </div>
            <CustomBadge
              title={getProjectStatusLabel(status) || status}
              statusType={status}
            />
          </div>
        </div>

        <div className={styles.infoContainer}>
          <div className="flex items-center gap-2 flex-wrap">
            <p className={styles.infoText}>
              Current Revision: <span>{getRevisionValue()}</span>
            </p>
            {revisionStatusLabel && status?.toLowerCase() !== "completed" && (
              <CustomBadge
                title={revisionStatusLabel}
                statusType={revisionStatusType}
              />
            )}
            {isDesigner && isWaitingForClientResponse() && (
              <CustomBadge
                title={
                  clientViewedAt
                    ? `Viewed: ${moment(clientViewedAt).format("MM/DD/YYYY @ HHmm")}`
                    : "Not Viewed"
                }
                bg={clientViewedAt ? "#E7F7ED" : "#F2F4F7"}
                textColor={clientViewedAt ? "#099D42" : "#475467"}
              />
            )}
          </div>
        </div>

        <div className={styles.statusBarContainer}>
          <div className={styles.statusBarWrapper}>
            <div className="w-full mt-3.5">
              <CustomStatusBar
                steps={steps}
                pendingIcon={<Icons.PendingState />}
                inProgressIcon={<Icons.InProgressState />}
                completeIcon={<Icons.CompleteState />}
                activeStep={activeStep}
                onStepChange={undefined}
              />
            </div>

            {/* <button
              className={styles.arrowButton}
              onClick={handleNextStep}
              disabled={activeStep === steps.length - 1 || isOnDetailPage}
            >
              <Icons.ChevRight />
            </button> */}
          </div>

          <div
            className={classNames(
              styles.actionContainer,
              "md:gap-2.5 gap-0 md:mt-0 sm:mt-6",
            )}
          >
            {canShowUploadRevision &&
              isPreliminaryOrLater &&
              !isOnDetailPage &&
              !archived &&
              !isWaitingForClientResponse() &&
              status?.toLowerCase() !== "completed" && (
                <div className="flex items-center gap-2 w-full md:mt-0 sm:mt-7 mt-2 w-full">
                  <CustomIconButton
                    Icon={<Icons.BlueFileUpload />}
                    fullWidthMobile={true}
                    text="Upload Revision"
                    tooltip="Upload Revision"
                    onClick={() => {
                      // A revision in review means the client's request is
                      // waiting for the designer to click "Start Working" —
                      // land on the Revision Requests tab in that case.
                      // Otherwise go straight to the upload flow.
                      if (isRevisionInReview()) {
                        navigate(
                          `${routeConstant.designer.projectDetail.path.replace(
                            ":id",
                            String(id || ""),
                          )}?tab=${ProjectDetailTabs.FEEDBACK_REQUEST}`,
                        );
                        return;
                      }
                      navigate(
                        `${routeConstant.designer.home.uploadRevision.path}?projectId=${id}`,
                        {
                          state: { projectId: id, projectTitle: title },
                        },
                      );
                    }}
                  />
                </div>
              )}

            {!isOnDetailPage ? (
              <div className="flex items-center justify-between gap-2 w-full md:mt-0 mt-2">
                {isDesigner ? (
                  <>
                    <div className="flex items-center gap-2">
                      <CustomIconButton
                        Icon={<Icons.Logout />}
                        fullWidthMobile={true}
                        text={hasJoined ? "Leave Project" : "Join Project"}
                        tooltip={hasJoined ? "Leave Project" : "Join Project"}
                        iconClassName={styles.joinLeaveIconButton}
                        onClick={() =>
                          hasJoined
                            ? handleProceedToLeaveProject()
                            : id != null && setJoinModalOpen(true)
                        }
                      />
                      {showArchiveButton && hasJoined && !archived && (
                        <CustomIconButton
                          Icon={<Icons.ArchiveIcon />}
                          outlined={true}
                          fullWidthMobile={true}
                          tooltip="Archive"
                          onClick={handleProceedToArchive}
                        />
                      )}
                    </div>
                    <CustomButton
                      IconDirection="left"
                      Icon={Icons.EyeProjectIcon}
                      title="View Project"
                      titleStyle={styles.titleLabel}
                      iconStyle={styles.outlinedButtonIcon}
                      onClick={() => {
                        if (onPrimaryAction) {
                          onPrimaryAction();
                        }
                      }}
                      containerStyle={styles.outlinedButton}
                    />
                    <JoinProjectModal
                      show={joinModalOpen}
                      handleClose={() => setJoinModalOpen(false)}
                      projectId={id!}
                      projectTitle={title}
                      projectImage={image}
                      onSuccess={(role) => {
                        setHasJoined(true);
                        onJoinSuccess?.(role);
                        onPrimaryAction?.();
                      }}
                    />
                    <ConfirmationModal
                      show={leaveModalOpen}
                      handleClose={() => setLeaveModalOpen(false)}
                      handleSubmit={handleConfirmLeaveProject}
                      title="Leave project?"
                      subtitle="Are you sure you want to leave this project? You will need to join again to access it."
                      actionButtonText="Yes, Leave"
                      cancelButtonText="Cancel"
                      loading={leaveLoading}
                      modalType="confirmation"
                    />
                    <ConfirmationModal
                      show={archiveModalOpen}
                      handleClose={() => setArchiveModalOpen(false)}
                      handleSubmit={handleConfirmArchiveToggle}
                      title="Archive project?"
                      subtitle="Are you sure you want to archive this project? Only an admin can unarchive it."
                      actionButtonText="Yes, Archive"
                      cancelButtonText="Cancel"
                      loading={archiveLoading}
                      modalType="confirmation"
                    />
                  </>
                ) : (
                  <CustomButton
                    IconDirection="left"
                    Icon={Icons.ArrowRightCircle}
                    title="View Project"
                    titleStyle={styles.titleLabel}
                    onClick={onPrimaryAction}
                    containerStyle={classNames(
                      styles.viewProjectButton,
                      "primary-btn",
                    )}
                  />
                )}
                <div className={classNames(styles.mobileNotification, "")}>
                  <button
                    onClick={handleChatIconClick}
                    className={classNames(
                      styles.notificationIcon,
                      "relative cursor-pointer",
                    )}
                    type="button"
                  >
                    <Icons.ChatNotificationIcon
                      className={styles.chatDotsAnimated}
                    />
                    {showDesignerNewWorkBubble && (
                      <span
                        className={styles.newRevisionDot}
                        aria-label="New client revision request"
                      />
                    )}
                    {hasUnreadChat && (
                      <span
                        className={styles.chatUnreadDot}
                        aria-label="New chat message"
                      />
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 w-full md:mt-0 sm:mt-8 mt-2">
                {canShowCompleteProjectButton && (
                  <>
                    <CustomButton
                      IconDirection="left"
                      Icon={Icons.CheckMarkCircleIcon}
                      title="Complete Project"
                      titleStyle={styles.submitRevision}
                      containerStyle={styles.nextPhaseButton}
                      onClick={handleProceedToCompleteProject}
                    />
                    {id && (
                      <FinalPlansModal
                        isOpen={completeModalOpen}
                        onClose={() => setCompleteModalOpen(false)}
                        projectId={Number(id)}
                        projectName={title}
                        onSuccess={handleCompleteProjectSuccess}
                      />
                    )}
                  </>
                )}
                <div className={classNames(styles.mobileNotification, "")}>
                  <button
                    onClick={handleChatIconClick}
                    className={classNames(
                      styles.notificationIcon,
                      "relative cursor-pointer",
                    )}
                    type="button"
                  >
                    <Icons.ChatNotificationIcon
                      className={styles.chatDotsAnimated}
                    />
                    {showDesignerNewWorkBubble && (
                      <span
                        className={styles.newRevisionDot}
                        aria-label="New client revision request"
                      />
                    )}
                    {hasUnreadChat && (
                      <span
                        className={styles.chatUnreadDot}
                        aria-label="New chat message"
                      />
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomProjectCard;
